<script setup>
defineProps({
  leftItems: { type: Array, required: true },
  rightItems: { type: Array, required: true },
  loading: { type: Boolean, default: false },
  leftTitle: { type: String, default: "Candidatos" },
  rightTitle: { type: String, default: "Seleccionados" },
  leftEmptyMessage: { type: String, default: "No hay elementos" },
  rightEmptyMessage: { type: String, default: "Ningún elemento seleccionado" },
});

defineEmits(["select", "unselect", "select-all", "unselect-all"]);

function itemKey(item) {
  return String(item?._id || "");
}
</script>

<template>
  <div class="dual-list-columns">
    <section class="dual-list-section">
      <div class="dual-list-header">
        <h6 class="dual-list-title">{{ leftTitle }}</h6>
        <span class="dual-list-count">
          <i class="fas fa-box" aria-hidden="true"></i>
          <span>{{ leftItems.length }}</span>
        </span>
      </div>
      <div class="dual-list-filters">
        <slot name="filters" />
        <button
          v-tooltip="'Seleccionar todos los bultos filtrados'"
          type="button"
          class="dual-list-move-all"
          :disabled="leftItems.length === 0"
          aria-label="Seleccionar todos los bultos filtrados"
          @click="$emit('select-all')"
        >
          <i class="fa-solid fa-angles-right" aria-hidden="true"></i>
        </button>
      </div>
      <div class="dual-list-box">
        <p v-if="loading" class="dual-list-empty">Cargando...</p>
        <p v-else-if="leftItems.length === 0" class="dual-list-empty">
          {{ leftEmptyMessage }}
        </p>
        <button
          v-for="item in leftItems"
          :key="itemKey(item)"
          type="button"
          class="dual-list-row"
          @click="$emit('select', item)"
        >
          <slot name="item" :item="item" />
        </button>
      </div>
    </section>

    <section class="dual-list-section">
      <div class="dual-list-header">
        <h6 class="dual-list-title">{{ rightTitle }}</h6>
        <span class="dual-list-count">
          <i class="fas fa-box" aria-hidden="true"></i>
          <span>{{ rightItems.length }}</span>
        </span>
      </div>
      <div class="dual-list-filters dual-list-filters--right">
        <button
          v-tooltip="'Quitar todos los bultos seleccionados'"
          type="button"
          class="dual-list-move-all"
          :disabled="rightItems.length === 0"
          aria-label="Quitar todos los bultos seleccionados"
          @click="$emit('unselect-all')"
        >
          <i class="fa-solid fa-angles-left" aria-hidden="true"></i>
        </button>
      </div>
      <div class="dual-list-box">
        <p v-if="rightItems.length === 0" class="dual-list-empty">
          {{ rightEmptyMessage }}
        </p>
        <button
          v-for="item in rightItems"
          :key="itemKey(item)"
          type="button"
          class="dual-list-row"
          @click="$emit('unselect', item)"
        >
          <slot name="item" :item="item" />
        </button>
      </div>
    </section>
  </div>
</template>

<style scoped lang="scss">
.dual-list-columns {
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 1rem;
  margin-top: 0.5rem;
}

.dual-list-section {
  background: #fff;
  border: 1px solid #dee2e6;
  border-radius: 0.5rem;
  min-width: 0;
  padding: 0.6rem;
}

.dual-list-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 0.15rem 0.4rem;
}

.dual-list-title {
  color: #67748e;
  font-size: 0.75rem;
  font-weight: 700;
  margin: 0;
  text-transform: uppercase;
}

.dual-list-count {
  align-items: center;
  border: 1px solid rgba(203, 213, 225, 0.9);
  border-radius: 999px;
  color: #67748e;
  display: inline-flex;
  flex-shrink: 0;
  font-size: 0.65rem;
  font-weight: 400;
  gap: 3px;
  line-height: 1;
  min-height: 20px;
  padding: 0 7px;
}

.dual-list-count i {
  font-size: 0.62rem;
  line-height: 1;
}

.dual-list-filters {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  min-height: 2.6rem;
  padding: 0;
}

.dual-list-filters--right {
  justify-content: flex-end;
}

.dual-list-move-all {
  flex-shrink: 0;
  align-items: center;
  background: #fff;
  border: 1px solid #dee2e6;
  border-radius: 0.35rem;
  color: #495057;
  font-size: 0.72rem;
  gap: 0.3rem;
  height: 1.5rem;
  padding: 0 0.6rem;
  display: inline-flex;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.dual-list-move-all:hover:not(:disabled) {
  background: #f8f9fa;
}

.dual-list-move-all:disabled {
  color: #adb5bd;
  cursor: default;
}

.dual-list-box {
  background: #f8fafc;
  border: 1px dashed rgba(191, 203, 217, 0.92);
  border-radius: 10px;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.82);
  height: 300px;
  margin-top: 0.5rem;
  overflow-y: auto;
  padding: 8px;
}

.dual-list-empty {
  font-size: 0.75rem;
  color: #8898aa;
  text-align: center;
  margin: 0.75rem 0;
}

.dual-list-row {
  background: #fff;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  border: 1px solid #dbe2ee;
  text-align: left;
  padding: 5px;
  border-radius: 10px;
  cursor: pointer;
  font-size: 0.78rem;
  line-height: 1.15;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.dual-list-row + .dual-list-row {
  margin-top: 0.4rem;
}

.dual-list-row:hover {
  border-color: #cbd5e1;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.05);
}

</style>
